import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import type { AfasDialog } from "@afassoftware/components";
import {
  ChatHomeTilesApiClient,
  type ChatHomeTileCmsTile,
  type ChatHomeTileSaveRequest,
  type ChatHomeTilesConfig,
  type ChatHomeTilesPlacementOption,
  type ChatHomeTilesScope,
} from "../cms/chat-home-tiles-api.js";
import {
  getChatHomeTilesErrorMessage,
  getChatHomeTilesErrorMessages,
  isConcurrencyConflict,
} from "../cms/chat-home-tiles-errors.js";
import { HelpLightElement } from "./help-light-element.js";

type TranslationDraft = {
  title: string;
  description: string;
  question: string;
};

type ContentDraft = {
  schemaVersion: number;
  translations: Record<string, TranslationDraft>;
  metadata: Record<string, unknown>;
  [key: string]: unknown;
};

type PlacementDraft = {
  country: string;
  product: string;
  sorting: number;
  isActive: boolean;
  placementJson: string;
};

type PublicImpactRow = {
  key: string;
  label: string;
  state: "wordt zichtbaar" | "wordt bijgewerkt" | "verdwijnt";
  cultures: string;
};

const DEFAULT_CONTENT: ContentDraft = {
  schemaVersion: 1,
  translations: {},
  metadata: {},
};

@customElement("help-jonas-home-tile-dialog")
export class HelpJonasHomeTileDialog extends HelpLightElement {
  @property({ attribute: false })
  accessor config: ChatHomeTilesConfig | null = null;

  @property({ attribute: false })
  accessor tiles: ChatHomeTileCmsTile[] = [];

  @property({ attribute: false })
  accessor currentScope: ChatHomeTilesScope | null = null;

  @property({ attribute: "anti-forgery-token" })
  accessor antiForgeryToken = "";

  @property({ type: Number, attribute: "max-public-tiles" })
  accessor maxPublicTiles = 3;

  @state()
  private accessor isOpen = false;

  @state()
  private accessor isSaving = false;

  @state()
  private accessor isDirty = false;

  @state()
  private accessor pendingDelete = false;

  @state()
  private accessor activationConfirmed = false;

  @state()
  private accessor concurrentChangeConfirmed = false;

  @state()
  private accessor selectedTileId: number | null = null;

  @state()
  private accessor contentDraft: ContentDraft = { ...DEFAULT_CONTENT };

  @state()
  private accessor placementDrafts: PlacementDraft[] = [];

  @state()
  private accessor validationErrors: string[] = [];

  @state()
  private accessor concurrentChangeWarning = "";

  private readonly api = new ChatHomeTilesApiClient();

  private readonly dialogRef: Ref<AfasDialog> = createRef();

  private selectedTileUpdatedUtc = "";

  private readonly handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.isDirty) return;
    event.preventDefault();
    event.returnValue = "";
  };

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  override disconnectedCallback(): void {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    super.disconnectedCallback();
  }

  openForCreate(): void {
    this.selectedTileId = null;
    this.selectedTileUpdatedUtc = "";
    this.contentDraft = createDefaultContent(this.currentScope?.culture);
    this.placementDrafts = this.currentScope
      ? [
          {
            country: this.currentScope.country,
            product: this.currentScope.product,
            sorting: this.getNextSorting(
              this.currentScope.country,
              this.currentScope.product,
            ),
            isActive: false,
            placementJson: "{}",
          },
        ]
      : [];
    this.validationErrors = [];
    this.concurrentChangeWarning = "";
    this.pendingDelete = false;
    this.activationConfirmed = false;
    this.concurrentChangeConfirmed = false;
    this.isDirty = false;
    this.isOpen = true;
  }

  openForEdit(tile: ChatHomeTileCmsTile): void {
    this.startEditingTile(tile, false);
  }

  openForDelete(tile: ChatHomeTileCmsTile): void {
    this.startEditingTile(tile, true);
  }

  protected override render() {
    return html`<afas-dialog
      ${ref(this.dialogRef)}
      data-chat-home-tile-dialog
      size="large"
      primary-title=${this.selectedTileId
        ? "Tegel bewerken"
        : "Tegel toevoegen"}
      .open=${this.isOpen}
      @dialog-close=${this.requestClose}
    >
      ${this.isOpen ? this.renderDialogContent() : nothing}
    </afas-dialog>`;
  }

  private renderDialogContent() {
    return html`<div class="cms-home-tile-dialog">
      ${this.renderValidationSummary()} ${this.renderConcurrentWarning()}
      ${this.renderSelector()} ${this.renderDeleteConfirmation()}
      ${this.renderActiveCountSummary()} ${this.renderActivationConfirmation()}
      <div class="cms-home-tile-dialog__editor">
        ${this.renderPlacementMatrix()} ${this.renderTranslationFields()}
        ${this.renderPublicImpactSummary()}
      </div>
      <footer class="cms-home-tile-dialog__footer">
        <afas-button
          label="Annuleren"
          data-action="cancel-edit"
          @click=${this.requestClose}
        ></afas-button>
        <afas-button
          label="Opslaan"
          variant="primary"
          data-action="save-tile"
          ?disabled=${this.isSaving}
          @click=${this.saveTile}
        ></afas-button>
      </footer>
    </div>`;
  }

  private renderSelector() {
    return html`<section
      class="cms-home-tile-dialog__selector"
      aria-label="Tegel kiezen"
    >
      <div class="cms-home-tile-dialog__selector-actions">
        <afas-button
          label="Nieuwe tegel"
          data-action="new-tile"
          @click=${this.openForCreate}
        >
          <afas-icon slot="icon" icon="AddOutlined"></afas-icon>
        </afas-button>
        <afas-button
          label="Geselecteerde tegel verwijderen"
          data-action="delete-selected-tile"
          ?disabled=${!this.selectedTileId || this.isSaving}
          @click=${this.requestDeleteSelectedTile}
        >
          <afas-icon slot="icon" icon="DeleteOutlined"></afas-icon>
        </afas-button>
      </div>
      <ul class="cms-home-tile-dialog__tile-list">
        ${this.tiles.map(
          (tile) =>
            html`<li>
              <afas-button
                label=${tile.title || `Tegel ${tile.id}`}
                data-action="select-tile"
                data-tile-id=${tile.id}
                @click=${() => this.selectExistingTile(tile)}
              ></afas-button>
            </li>`,
        )}
      </ul>
    </section>`;
  }

  private renderDeleteConfirmation() {
    if (!this.pendingDelete || !this.selectedTileId) return nothing;

    return html`<afas-alert type="warning" data-delete-confirmation>
      Deze tegel wordt verwijderd uit alle plaatsingen.
      <afas-button
        label="Verwijderen bevestigen"
        data-action="confirm-delete"
        ?disabled=${this.isSaving}
        @click=${this.deleteTile}
      ></afas-button>
    </afas-alert>`;
  }

  private renderPlacementMatrix() {
    const countries = this.getPlacementGroups();
    return html`<section data-placement-matrix>
      <header class="cms-home-tile-dialog__section-header">
        <h3>Plaatsingen</h3>
        <div class="cms-home-tile-dialog__bulk-actions">
          <afas-button
            label="Huidig land"
            data-action="select-current-country"
            @click=${this.selectCurrentCountryPlacements}
          ></afas-button>
          <afas-button
            label=${this.getCurrentProductBulkLabel()}
            data-action="select-current-product"
            @click=${this.selectCurrentProductPlacements}
          ></afas-button>
          <afas-button
            label="Leegmaken"
            data-action="clear-placements"
            @click=${this.clearPlacements}
          ></afas-button>
        </div>
      </header>
      ${countries.map(
        ([country, placements]) =>
          html`<section data-placement-country=${country}>
            <h4>${placements[0]?.helpCenterLabel ?? country}</h4>
            <div class="cms-home-tile-dialog__placement-grid">
              <div
                class="cms-home-tile-dialog__placement-header"
                data-placement-column-header
              >
                <span>Product</span>
                <span>Sortering</span>
                <span>Actief</span>
              </div>
              ${placements.map((placement) =>
                this.renderPlacementOption(placement),
              )}
            </div>
          </section>`,
      )}
    </section>`;
  }

  private renderPlacementOption(placement: ChatHomeTilesPlacementOption) {
    const draft = this.findPlacementDraft(placement.country, placement.product);
    return html`<div
      class="cms-home-tile-dialog__placement-row"
      data-placement-product=${placement.product}
    >
      <div class="cms-home-tile-dialog__placement-product">
        <afas-checkbox
          .value=${Boolean(draft)}
          label=${`Plaatsing ${placement.productLabel}`}
          @change=${(event: CustomEvent<{ value?: boolean }>) =>
            this.setPlacementSelected(
              placement,
              Boolean(event.detail?.value ?? !draft),
            )}
        ></afas-checkbox>
        <span
          class="cms-home-tile-dialog__placement-product-label"
          data-placement-product-label
          >${placement.productLabel}</span
        >
      </div>
      <afas-text-input
        label="Sortering"
        type="number"
        .value=${String(
          draft?.sorting ??
            this.getNextSorting(placement.country, placement.product),
        )}
        ?disabled=${!draft}
        @change=${(event: Event) =>
          this.setPlacementSorting(placement, event.currentTarget)}
      ></afas-text-input>
      <afas-checkbox
        .value=${Boolean(draft?.isActive)}
        label="Actief"
        ?disabled=${!draft}
        @change=${(event: CustomEvent<{ value?: boolean }>) =>
          this.setPlacementActive(placement, Boolean(event.detail?.value))}
      ></afas-checkbox>
    </div>`;
  }

  private renderTranslationFields() {
    const cultures = this.getRequiredCultures();
    return html`<section class="cms-home-tile-dialog__translations">
      <h3>Vertalingen</h3>
      ${cultures.map(
        (culture) =>
          html`<fieldset data-translation-culture=${culture.culture}>
            <legend>${culture.label}</legend>
            <afas-text-input
              label="Titel"
              .value=${this.getTranslation(culture.culture).title}
              @change=${(event: Event) =>
                this.setTranslationValue(
                  culture.culture,
                  "title",
                  event.currentTarget,
                )}
            ></afas-text-input>
            <afas-textarea-input
              label="Omschrijving"
              .value=${this.getTranslation(culture.culture).description}
              @change=${(event: Event) =>
                this.setTranslationValue(
                  culture.culture,
                  "description",
                  event.currentTarget,
                )}
            ></afas-textarea-input>
            <afas-textarea-input
              label="Verborgen vraag"
              .value=${this.getTranslation(culture.culture).question}
              @change=${(event: Event) =>
                this.setTranslationValue(
                  culture.culture,
                  "question",
                  event.currentTarget,
                )}
            ></afas-textarea-input>
          </fieldset>`,
      )}
    </section>`;
  }

  private renderValidationSummary() {
    if (!this.validationErrors.length) return nothing;
    return html`<afas-alert type="danger" data-validation-summary>
      <ul>
        ${this.validationErrors.map((error) => html`<li>${error}</li>`)}
      </ul>
    </afas-alert>`;
  }

  private renderConcurrentWarning() {
    if (!this.concurrentChangeWarning) return nothing;
    return html`<afas-alert type="warning" data-concurrent-change-warning>
      <p>${this.concurrentChangeWarning}</p>
      <afas-button
        label="Doorgaan met mijn wijzigingen"
        data-action="continue-after-concurrent-change"
        @click=${this.continueAfterConcurrentChange}
      ></afas-button>
      <afas-button
        label="Laatste versie laden"
        data-action="reload-latest-tile"
        @click=${this.reloadLatestTile}
      ></afas-button>
      <afas-button
        label="Annuleren"
        data-action="cancel-edit"
        @click=${this.requestClose}
      ></afas-button>
    </afas-alert>`;
  }

  private renderActiveCountSummary() {
    if (!this.currentScope) return nothing;

    const activeCount = this.getCurrentScopeActiveCount();
    const exceedsMax = this.wouldExceedMaxPublicTiles();
    return html`<afas-alert
      type=${exceedsMax || activeCount >= this.maxPublicTiles
        ? "warning"
        : "info"}
      data-active-count-summary
    >
      ${activeCount} van maximaal ${this.maxPublicTiles} actieve tegels voor
      ${this.currentScope.helpCenterLabel} / ${this.currentScope.productLabel}.
    </afas-alert>`;
  }

  private renderActivationConfirmation() {
    if (!this.requiresActivationConfirmation() || this.activationConfirmed) {
      return nothing;
    }

    return html`<afas-alert type="warning" data-activation-confirmation>
      Actieve plaatsingen worden direct zichtbaar voor bezoekers na succesvol
      opslaan en cacheverversing.
      <afas-button
        label="Publicatie bevestigen"
        data-action="confirm-public-activation"
        @click=${this.confirmPublicActivation}
      ></afas-button>
    </afas-alert>`;
  }

  private renderPublicImpactSummary() {
    const rows = this.getPublicImpactRows();
    if (!rows.length) return nothing;

    return html`<afas-alert type="warning" data-public-impact-summary>
      <p>
        Opslaan past de publieke zichtbaarheid direct aan na succesvol opslaan
        en cacheverversing.
      </p>
      <ul>
        ${rows.map(
          (row) =>
            html`<li
              data-impact-state=${row.state}
              data-impact-cultures=${row.cultures}
            >
              ${row.label}: ${row.state} (${row.cultures})
            </li>`,
        )}
      </ul>
    </afas-alert>`;
  }

  private startEditingTile(
    tile: ChatHomeTileCmsTile,
    pendingDelete: boolean,
  ): void {
    this.selectedTileId = tile.id;
    this.selectedTileUpdatedUtc = tile.updatedUtc;
    this.contentDraft = parseContentDraft(tile.contentJson);
    this.placementDrafts = tile.placements.map((placement) => ({
      country: placement.country,
      product: placement.product,
      sorting: placement.sorting,
      isActive: placement.isActive,
      placementJson: placement.placementJson || "{}",
    }));
    this.validationErrors = [];
    this.concurrentChangeWarning = "";
    this.pendingDelete = pendingDelete;
    this.activationConfirmed = false;
    this.concurrentChangeConfirmed = false;
    this.isDirty = false;
    this.isOpen = true;
  }

  private selectExistingTile(tile: ChatHomeTileCmsTile): void {
    if (!this.canDiscardChanges()) return;
    this.startEditingTile(tile, false);
  }

  private requestDeleteSelectedTile = (): void => {
    if (!this.selectedTileId) return;
    this.pendingDelete = true;
  };

  private deleteTile = async (): Promise<void> => {
    if (!this.selectedTileId) return;
    this.isSaving = true;
    this.validationErrors = [];
    try {
      await this.api.deleteTile(this.selectedTileId, this.antiForgeryToken);
      this.isDirty = false;
      this.isOpen = false;
      this.dispatchEvent(
        new CustomEvent("chat-home-tile-deleted", {
          bubbles: true,
          composed: true,
          detail: { id: this.selectedTileId },
        }),
      );
    } catch (error) {
      this.validationErrors = getChatHomeTilesErrorMessages(
        error,
        "Tegel kon niet worden verwijderd.",
      );
    } finally {
      this.isSaving = false;
    }
  };

  private saveTile = async (): Promise<void> => {
    const validationErrors = this.validateDraft();
    if (
      this.requiresConcurrentChangeConfirmation() &&
      !this.concurrentChangeConfirmed
    ) {
      this.concurrentChangeWarning =
        "Deze tegel is gewijzigd sinds je de editor opende. Controleer de laatste versie voordat je opslaat.";
      validationErrors.push(
        "Bevestig of herlaad de gewijzigde tegel voordat je opslaat.",
      );
    }

    if (validationErrors.length) {
      this.validationErrors = validationErrors;
      return;
    }

    this.isSaving = true;
    this.validationErrors = [];
    try {
      const request = this.createSaveRequest();
      const savedTile = this.selectedTileId
        ? await this.api.updateTile(
            this.selectedTileId,
            request,
            this.antiForgeryToken,
          )
        : await this.api.saveTile(request, this.antiForgeryToken);
      this.isDirty = false;
      this.isOpen = false;
      this.dispatchEvent(
        new CustomEvent("chat-home-tile-saved", {
          bubbles: true,
          composed: true,
          detail: { tile: savedTile },
        }),
      );
    } catch (error) {
      if (isConcurrencyConflict(error)) {
        this.handleConcurrencyConflict(error);
      } else {
        this.validationErrors = getChatHomeTilesErrorMessages(
          error,
          "Tegel kon niet worden opgeslagen.",
        );
      }
    } finally {
      this.isSaving = false;
    }
  };

  private handleConcurrencyConflict = (error: unknown): void => {
    this.concurrentChangeConfirmed = false;
    this.concurrentChangeWarning = getChatHomeTilesErrorMessage(
      error,
      "Deze tegel is gewijzigd door een andere sessie. Laad de laatste versie en probeer het opnieuw.",
    );
    this.validationErrors = [];
    // Ask the manager to refresh the tiles so "reload latest" and the client-side stale
    // check reflect the newest server version; the dialog stays open with the draft intact.
    this.dispatchEvent(
      new CustomEvent("chat-home-tile-stale", {
        bubbles: true,
        composed: true,
        detail: { id: this.selectedTileId },
      }),
    );
  };

  private requestClose = (): void => {
    if (!this.canDiscardChanges()) {
      // The afas-dialog's built-in close button hides itself before it dispatches
      // dialog-close, so re-open it when the editor chose to keep its changes.
      const dialog = this.dialogRef.value;
      if (dialog && !dialog.open) dialog.show();
      return;
    }
    this.isOpen = false;
    this.pendingDelete = false;
    this.activationConfirmed = false;
    this.concurrentChangeConfirmed = false;
  };

  private canDiscardChanges(): boolean {
    return (
      !this.isDirty || window.confirm("Niet-opgeslagen wijzigingen weggooien?")
    );
  }

  private createSaveRequest(): ChatHomeTileSaveRequest {
    // Send the loaded UpdatedUtc so the server rejects a stale overwrite (409). When the
    // editor has explicitly confirmed "continue with my changes" after a conflict, omit it
    // to force a last-write-wins overwrite of the newer version.
    const enforceConcurrency =
      Boolean(this.selectedTileId) &&
      Boolean(this.selectedTileUpdatedUtc) &&
      !this.concurrentChangeConfirmed;
    return {
      ...(this.selectedTileId ? { id: this.selectedTileId } : {}),
      ...(enforceConcurrency
        ? { expectedUpdatedUtc: this.selectedTileUpdatedUtc }
        : {}),
      contentJson: JSON.stringify(this.contentDraft),
      placements: this.placementDrafts.map((placement) => ({
        country: placement.country,
        product: placement.product,
        sorting: placement.sorting,
        isActive: placement.isActive,
        placementJson: placement.placementJson,
      })),
    };
  }

  private validateDraft(): string[] {
    const errors: string[] = [];
    if (!this.placementDrafts.length) {
      errors.push("Kies minimaal een plaatsing.");
    }

    for (const culture of this.getRequiredCultures()) {
      const translation = this.getTranslation(culture.culture);
      if (!translation.title.trim())
        errors.push(`${culture.label}: titel is verplicht.`);
      if (!translation.description.trim())
        errors.push(`${culture.label}: omschrijving is verplicht.`);
      if (!translation.question.trim())
        errors.push(`${culture.label}: verborgen vraag is verplicht.`);
    }

    if (this.wouldExceedMaxPublicTiles()) {
      errors.push(
        `Er zijn meer dan ${this.maxPublicTiles} actieve tegels voor de huidige scope.`,
      );
    }

    if (this.requiresActivationConfirmation() && !this.activationConfirmed) {
      errors.push(
        "Bevestig dat actieve plaatsingen direct gepubliceerd worden.",
      );
    }

    return errors;
  }

  private setPlacementSelected(
    placement: ChatHomeTilesPlacementOption,
    selected: boolean,
  ): void {
    const existing = this.findPlacementDraft(
      placement.country,
      placement.product,
    );
    if (selected && !existing) {
      this.placementDrafts = [
        ...this.placementDrafts,
        {
          country: placement.country,
          product: placement.product,
          sorting: this.getNextSorting(placement.country, placement.product),
          isActive: false,
          placementJson: "{}",
        },
      ];
    }

    if (!selected && existing) {
      this.placementDrafts = this.placementDrafts.filter(
        (draft) => draft !== existing,
      );
    }

    this.markDirty();
  }

  private setPlacementSorting(
    placement: ChatHomeTilesPlacementOption,
    target: EventTarget | null,
  ): void {
    const sorting = Number(getElementValue(target));
    this.updatePlacement(placement, {
      sorting: Number.isFinite(sorting) && sorting >= 0 ? sorting : 0,
    });
  }

  private setPlacementActive(
    placement: ChatHomeTilesPlacementOption,
    isActive: boolean,
  ): void {
    this.updatePlacement(placement, { isActive });
  }

  private confirmPublicActivation = (): void => {
    this.activationConfirmed = true;
    this.validationErrors = [];
  };

  private continueAfterConcurrentChange = (): void => {
    this.concurrentChangeConfirmed = true;
    this.concurrentChangeWarning = "";
    this.validationErrors = [];
  };

  private reloadLatestTile = (): void => {
    const selectedTile = this.getSelectedTile();
    if (selectedTile) this.startEditingTile(selectedTile, false);
  };

  private updatePlacement(
    placement: ChatHomeTilesPlacementOption,
    patch: Partial<PlacementDraft>,
  ): void {
    this.placementDrafts = this.placementDrafts.map((draft) =>
      draft.country === placement.country && draft.product === placement.product
        ? { ...draft, ...patch }
        : draft,
    );
    this.markDirty();
  }

  private setTranslationValue(
    culture: string,
    field: keyof TranslationDraft,
    target: EventTarget | null,
  ): void {
    const translation = this.getTranslation(culture);
    this.contentDraft = {
      ...this.contentDraft,
      translations: {
        ...this.contentDraft.translations,
        [culture]: {
          ...translation,
          [field]: getElementValue(target),
        },
      },
    };
    this.markDirty();
  }

  private selectCurrentCountryPlacements = (): void => {
    if (!this.currentScope || !this.config) return;
    this.selectPlacements(
      this.getPlacementOptions().filter(
        (placement) => placement.country === this.currentScope?.country,
      ),
    );
  };

  private selectCurrentProductPlacements = (): void => {
    if (!this.currentScope || !this.config) return;
    this.selectPlacements(
      this.getPlacementOptions().filter(
        (placement) => placement.product === this.currentScope?.product,
      ),
    );
  };

  private getCurrentProductBulkLabel(): string {
    return this.currentScope
      ? `Alle ${this.currentScope.productLabel}`
      : "Huidig product";
  }

  private clearPlacements = (): void => {
    this.placementDrafts = [];
    this.markDirty();
  };

  private selectPlacements(placements: ChatHomeTilesPlacementOption[]): void {
    const drafts = new Map(
      this.placementDrafts.map((placement) => [
        `${placement.country}/${placement.product}`,
        placement,
      ]),
    );

    for (const placement of placements) {
      const key = `${placement.country}/${placement.product}`;
      if (!drafts.has(key)) {
        drafts.set(key, {
          country: placement.country,
          product: placement.product,
          sorting: this.getNextSorting(placement.country, placement.product),
          isActive: false,
          placementJson: "{}",
        });
      }
    }

    this.placementDrafts = [...drafts.values()];
    this.markDirty();
  }

  private findPlacementDraft(
    country: string,
    product: string,
  ): PlacementDraft | null {
    return (
      this.placementDrafts.find(
        (placement) =>
          placement.country === country && placement.product === product,
      ) ?? null
    );
  }

  private getPlacementGroups(): Array<
    [string, ChatHomeTilesPlacementOption[]]
  > {
    const groups = new Map<string, ChatHomeTilesPlacementOption[]>();
    for (const placement of this.getPlacementOptions()) {
      const group = groups.get(placement.country) ?? [];
      group.push(placement);
      groups.set(placement.country, group);
    }
    return [...groups.entries()];
  }

  private getPlacementOptions(): ChatHomeTilesPlacementOption[] {
    const placements = new Map<string, ChatHomeTilesPlacementOption>();
    for (const placement of this.config?.placements ?? []) {
      placements.set(`${placement.country}/${placement.product}`, placement);
    }

    for (const scope of this.config?.scopes ?? []) {
      const key = `${scope.country}/${scope.product}`;
      if (placements.has(key)) continue;
      placements.set(key, this.createPlacementOptionFromScope(scope));
    }

    return [...placements.values()];
  }

  private createPlacementOptionFromScope(
    scope: ChatHomeTilesScope,
  ): ChatHomeTilesPlacementOption {
    return {
      country: scope.country,
      product: scope.product,
      countryLabel: scope.countryLabel,
      helpCenterLabel: scope.helpCenterLabel,
      productLabel: scope.productLabel,
      productShortLabel: scope.productShortLabel,
      searchArea: scope.searchArea,
      supportedCultures: (this.config?.scopes ?? [])
        .filter(
          (candidate) =>
            candidate.country === scope.country &&
            candidate.product === scope.product,
        )
        .map((candidate) => ({
          culture: candidate.culture,
          label: candidate.cultureLabel,
          shortLabel: candidate.cultureShortLabel,
          languageId: candidate.languageId,
          isDefault: candidate.isDefaultCulture,
        })),
    };
  }

  private getRequiredCultures(): Array<{
    culture: string;
    label: string;
    shortLabel: string;
  }> {
    const cultures = new Map<
      string,
      { culture: string; label: string; shortLabel: string }
    >();
    for (const draft of this.placementDrafts) {
      const placement = this.getPlacementOptions().find(
        (candidate) =>
          candidate.country === draft.country &&
          candidate.product === draft.product,
      );
      for (const culture of placement?.supportedCultures ?? []) {
        cultures.set(culture.culture, culture);
      }
    }

    if (!cultures.size && this.currentScope) {
      cultures.set(this.currentScope.culture, {
        culture: this.currentScope.culture,
        label: this.currentScope.cultureLabel,
        shortLabel: this.currentScope.cultureShortLabel,
      });
    }

    return [...cultures.values()];
  }

  private getTranslation(culture: string): TranslationDraft {
    return (
      this.contentDraft.translations[culture] ?? {
        title: "",
        description: "",
        question: "",
      }
    );
  }

  private getPlacementLabel(placement: PlacementDraft): string {
    const option = this.getPlacementOptions().find(
      (candidate) =>
        candidate.country === placement.country &&
        candidate.product === placement.product,
    );
    return option
      ? `${option.helpCenterLabel} / ${option.productLabel}`
      : `${placement.country}/${placement.product}`;
  }

  private getCurrentScopeActiveCount(): number {
    if (!this.currentScope) return 0;

    const otherActiveTiles = this.tiles.filter(
      (tile) =>
        tile.id !== this.selectedTileId &&
        tile.placements.some(
          (placement) =>
            placement.country === this.currentScope?.country &&
            placement.product === this.currentScope?.product &&
            placement.isActive,
        ),
    ).length;
    const draftIsActiveForScope = this.placementDrafts.some(
      (placement) =>
        placement.country === this.currentScope?.country &&
        placement.product === this.currentScope?.product &&
        placement.isActive,
    );

    return otherActiveTiles + (draftIsActiveForScope ? 1 : 0);
  }

  private wouldExceedMaxPublicTiles(): boolean {
    return this.getCurrentScopeActiveCount() > this.maxPublicTiles;
  }

  private requiresActivationConfirmation(): boolean {
    return this.placementDrafts.some((placement) => {
      if (!placement.isActive) return false;
      const existingPlacement = this.getSelectedTilePlacement(
        placement.country,
        placement.product,
      );
      return !existingPlacement?.isActive;
    });
  }

  private requiresConcurrentChangeConfirmation(): boolean {
    const selectedTile = this.getSelectedTile();
    return Boolean(
      selectedTile &&
      this.selectedTileUpdatedUtc &&
      selectedTile.updatedUtc !== this.selectedTileUpdatedUtc,
    );
  }

  private getPublicImpactRows(): PublicImpactRow[] {
    const rows: PublicImpactRow[] = [];
    const contentChanged = this.isContentChanged();

    for (const placement of this.placementDrafts) {
      if (!placement.isActive) continue;
      const existingPlacement = this.getSelectedTilePlacement(
        placement.country,
        placement.product,
      );

      if (!existingPlacement?.isActive) {
        rows.push({
          key: `${placement.country}/${placement.product}`,
          label: this.getPlacementLabel(placement),
          state: "wordt zichtbaar",
          cultures: this.getPlacementCultureLabels(
            placement.country,
            placement.product,
          ),
        });
        continue;
      }

      // The placement was already public and stays active, so it only counts as an
      // update when the public output actually changes: the tile content changed, or
      // this placement's order/config changed. Otherwise nothing changes for it.
      const placementChanged =
        existingPlacement.sorting !== placement.sorting ||
        (existingPlacement.placementJson || "{}") !==
          (placement.placementJson || "{}");
      if (!contentChanged && !placementChanged) continue;

      rows.push({
        key: `${placement.country}/${placement.product}`,
        label: this.getPlacementLabel(placement),
        state: "wordt bijgewerkt",
        cultures: this.getPlacementCultureLabels(
          placement.country,
          placement.product,
        ),
      });
    }

    for (const existingPlacement of this.getSelectedTile()?.placements ?? []) {
      if (!existingPlacement.isActive) continue;
      const draft = this.findPlacementDraft(
        existingPlacement.country,
        existingPlacement.product,
      );
      if (draft?.isActive) continue;

      rows.push({
        key: `${existingPlacement.country}/${existingPlacement.product}`,
        label: this.getPlacementLabel({
          country: existingPlacement.country,
          product: existingPlacement.product,
          sorting: existingPlacement.sorting,
          isActive: false,
          placementJson: existingPlacement.placementJson,
        }),
        state: "verdwijnt",
        cultures: this.getPlacementCultureLabels(
          existingPlacement.country,
          existingPlacement.product,
        ),
      });
    }

    return rows;
  }

  private isContentChanged(): boolean {
    const savedTile = this.getSelectedTile();
    if (!savedTile) return true;
    return (
      JSON.stringify(this.contentDraft) !==
      JSON.stringify(parseContentDraft(savedTile.contentJson))
    );
  }

  private getSelectedTile(): ChatHomeTileCmsTile | null {
    return this.tiles.find((tile) => tile.id === this.selectedTileId) ?? null;
  }

  private getSelectedTilePlacement(country: string, product: string) {
    return (
      this.getSelectedTile()?.placements.find(
        (placement) =>
          placement.country === country && placement.product === product,
      ) ?? null
    );
  }

  private getPlacementCultureLabels(country: string, product: string): string {
    const placement = this.getPlacementOptions().find(
      (candidate) =>
        candidate.country === country && candidate.product === product,
    );
    return (
      placement?.supportedCultures
        .map((culture) => culture.shortLabel)
        .join(", ") ?? ""
    );
  }

  private getNextSorting(country?: string, product?: string): number {
    const sortingValues = [
      ...this.tiles
        .flatMap((tile) => tile.placements)
        .filter(
          (placement) =>
            (!country || placement.country === country) &&
            (!product || placement.product === product),
        )
        .map((placement) => placement.sorting),
      ...this.placementDrafts
        .filter(
          (placement) =>
            (!country || placement.country === country) &&
            (!product || placement.product === product),
        )
        .map((placement) => placement.sorting),
    ];

    return sortingValues.length ? Math.max(...sortingValues) + 10 : 10;
  }

  private markDirty(): void {
    this.isDirty = true;
    this.pendingDelete = false;
    this.activationConfirmed = false;
    this.concurrentChangeConfirmed = false;
    this.concurrentChangeWarning = "";
    this.validationErrors = [];
  }
}

function createDefaultContent(culture = "nl"): ContentDraft {
  return {
    schemaVersion: 1,
    translations: {
      [culture]: { title: "", description: "", question: "" },
    },
    metadata: {},
  };
}

function parseContentDraft(contentJson: string): ContentDraft {
  try {
    const parsed = JSON.parse(contentJson) as Record<string, unknown>;
    const translations = parseTranslations(parsed.translations);
    return {
      ...parsed,
      schemaVersion: 1,
      translations,
      metadata: isRecord(parsed.metadata) ? parsed.metadata : {},
    };
  } catch {
    return createDefaultContent();
  }
}

function parseTranslations(value: unknown): Record<string, TranslationDraft> {
  if (!isRecord(value)) return {};

  const translations: Record<string, TranslationDraft> = {};
  for (const [culture, translation] of Object.entries(value)) {
    if (!isRecord(translation)) continue;
    translations[culture] = {
      title: getString(translation.title),
      description: getString(translation.description),
      question: getString(translation.question),
    };
  }

  return translations;
}

function getElementValue(target: EventTarget | null): string {
  if (target && "value" in target) {
    return String((target as { value: unknown }).value ?? "");
  }

  return "";
}

function getString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-home-tile-dialog": HelpJonasHomeTileDialog;
  }
}
